import { useMutation, useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { CuriosityAutocomplete, CuriosityNamespaceType, useCuriosityConfig } from '@procureai/curiosity-ui-mui';
import { CuriosityTreeSelectField, CuriosityTreeSelectMultipleField } from '@procureai/ui-form-mui/fields';
import {
  PageHeaderStatic,
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetIcon,
  useCustomSnackbar,
} from '@procureai/ui-mui';
import { Button, Input, Label, useConfirmationDialog } from '@procureai/uix';
import { UsersIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DataAccessManagementDomainType, I18nNamespace } from '~/common/types';
import { DomainIcon } from '~/components/DomainIcon/DomainIcon';
import { PageLayout } from '~/components/PageLayout/PageLayout';

import { DELETE_TEAM_MUTATION, TEAM_QUERY, UPSERT_TEAM_MUTATION } from './TeamManagement.gql';
import { mapFormToUpsertInput, mapTeamToFormData } from './TeamManagement.helpers';
import { teamManagementFormDefaults, TeamManagementFormData, teamManagementFormSchema } from './TeamManagement.schema';

export const TeamManagement = () => {
  const { t } = useTranslation(I18nNamespace.common);
  const { onError, enqueueSuccessSnackbar, enqueueErrorSnackbar } = useCustomSnackbar();
  const { cache: curiosityCache } = useCuriosityConfig();
  const { confirm } = useConfirmationDialog();
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [teamSelectRenderKey, setTeamSelectRenderKey] = useState(0);
  const [memberTeamsRenderKey, setMemberTeamsRenderKey] = useState(0);
  const isEditMode = !!selectedTeamId;

  const form = useForm<TeamManagementFormData>({
    mode: 'onTouched',
    resolver: zodResolver(teamManagementFormSchema),
    defaultValues: teamManagementFormDefaults,
  });

  const { data: teamData, refetch: refetchTeam } = useQuery(TEAM_QUERY, {
    variables: { teamId: selectedTeamId as string },
    skip: !selectedTeamId,
    onError,
  });

  const [upsertTeam, { loading: saving }] = useMutation(UPSERT_TEAM_MUTATION, { onError });
  const [deleteTeam, { loading: deleting }] = useMutation(DELETE_TEAM_MUTATION, { onError });

  useEffect(() => {
    if (!selectedTeamId) {
      form.reset(teamManagementFormDefaults);
      return;
    }

    if (!teamData?.Pasa?.team) {
      return;
    }

    form.reset(mapTeamToFormData(teamData.Pasa.team));
  }, [form, selectedTeamId, teamData?.Pasa?.team]);

  const handleSubmit = form.handleSubmit(async (values) => {
    const input = mapFormToUpsertInput(values);

    const isCreateMode = !values.id;
    const response = await upsertTeam({ variables: { input } });

    if (!response?.data?.Pasa?.upsertTeam?.id) {
      enqueueErrorSnackbar(response.errors?.map((error) => error.message).join('\n') ?? t('TeamManagement.saveError'));
      return;
    }
    const upsertedTeam = response.data?.Pasa?.upsertTeam;

    const createdOrUpdatedTeamId = upsertedTeam.id;
    const savedTeamName = upsertedTeam.name ?? input.name;

    // Keep the curiosity tree metadata in sync after save so the selectors
    // immediately reflect renamed teams and child-count changes without a reload.
    const cachedTeam = curiosityCache.get(CuriosityNamespaceType.DATA_ACCESS_TEAM, createdOrUpdatedTeamId);

    curiosityCache.set(CuriosityNamespaceType.DATA_ACCESS_TEAM, createdOrUpdatedTeamId, {
      id: cachedTeam?.id ?? createdOrUpdatedTeamId,
      key: cachedTeam?.key ?? createdOrUpdatedTeamId,
      value: savedTeamName,
      entity: {
        childrenCount: upsertedTeam.teamMemberIds.length,
        path: cachedTeam?.entity?.path ?? [],
        parent: cachedTeam?.entity?.parent ?? null,
      },
      language: cachedTeam?.language ?? '',
    });
    await refetchTeam({ teamId: createdOrUpdatedTeamId });
    // Force both tree selects to remount so they pick up the refreshed cache state
    // after team membership changes.
    setTeamSelectRenderKey((currentValue) => currentValue + 1);
    setMemberTeamsRenderKey((currentValue) => currentValue + 1);

    enqueueSuccessSnackbar(t('TeamManagement.saveSuccess'));

    if (isCreateMode) {
      setSelectedTeamId(createdOrUpdatedTeamId);
    }
  });

  const handleDelete = () => {
    if (!selectedTeamId) {
      return;
    }

    const teamNameForConfirmation = form.getValues('name') || teamData?.Pasa?.team?.name || selectedTeamId;

    confirm(
      async () => {
        const response = await deleteTeam({ variables: { teamId: selectedTeamId } });

        if (!response?.data?.Pasa?.deleteTeam?.id) {
          enqueueErrorSnackbar(
            response.errors?.map((error) => error.message).join('\n') ?? t('TeamManagement.deleteError'),
          );
          return;
        }

        setSelectedTeamId(null);
        form.reset(teamManagementFormDefaults);
        setTeamSelectRenderKey((currentValue) => currentValue + 1);
        setMemberTeamsRenderKey((currentValue) => currentValue + 1);
        enqueueSuccessSnackbar(t('TeamManagement.deleteSuccess'));
      },
      {
        title: t('TeamManagement.deleteConfirmTitle'),
        message: t('TeamManagement.deleteConfirmMessage', { teamName: teamNameForConfirmation }),
      },
    )();
  };

  return (
    <PageLayout
      fullHeight
      renderHeader={
        <PageHeaderStatic
          title={t('Nav.teamManagement')}
          maxWidth={false}
          icon={
            <WidgetIcon
              icon={<DomainIcon domain={DataAccessManagementDomainType.TEAMS_MANAGEMENT} />}
              color="primary"
            />
          }
        />
      }
    >
      <Widget data-intercom-target="TeamManagementWidget">
        <WidgetHeader title={t('Nav.teamManagement')} icon={<WidgetIcon icon={<UsersIcon size={16} />} />} />
        <WidgetContent>
          <div className="space-y-4">
            <CuriosityTreeSelectField
              key={teamSelectRenderKey}
              ns={CuriosityNamespaceType.DATA_ACCESS_TEAM}
              value={selectedTeamId}
              onChange={(value) => setSelectedTeamId(value ?? null)}
              label={t('TeamManagement.currentTeamLabel')}
              helperText={t('TeamManagement.currentTeamHelper')}
              placeholder={t('TeamManagement.currentTeamPlaceholder')}
              clearable
            />

            <form onSubmit={handleSubmit} noValidate>
              <div className="space-y-3">
                <p className="text-sm font-medium">
                  {selectedTeamId ? t('TeamManagement.editTitle') : t('TeamManagement.createTitle')}
                </p>

                <Controller
                  name="name"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <div className="space-y-1.5">
                      <Label htmlFor="team-management-name">
                        {t('TeamManagement.nameLabel')} <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        {...field}
                        id="team-management-name"
                        required
                        aria-invalid={fieldState.error ? 'true' : 'false'}
                      />
                      {fieldState.error ? (
                        <p className="text-xs text-destructive">{t('TeamManagement.nameRequired')}</p>
                      ) : null}
                    </div>
                  )}
                />

                <Controller
                  name="memberUserIds"
                  control={form.control}
                  render={({ field }) => (
                    <CuriosityAutocomplete
                      {...field}
                      ns={CuriosityNamespaceType.INTERNAL_USER}
                      multiple
                      simplifyValue
                      TextFieldProps={{
                        label: t('TeamManagement.memberUsersLabel'),
                        helperText: t('TeamManagement.memberUsersHelper'),
                      }}
                      value={field.value ?? []}
                      onChange={(value) => field.onChange(value ?? [])}
                    />
                  )}
                />

                <Controller
                  name="memberTeamIds"
                  control={form.control}
                  render={({ field }) => (
                    <CuriosityTreeSelectMultipleField
                      key={memberTeamsRenderKey}
                      ns={CuriosityNamespaceType.DATA_ACCESS_TEAM}
                      value={field.value}
                      onChange={(value) => {
                        const nextValue = value ?? [];
                        field.onChange(
                          selectedTeamId ? nextValue.filter((teamId) => teamId !== selectedTeamId) : nextValue,
                        );
                      }}
                      label={t('TeamManagement.memberTeamsLabel')}
                      helperText={t('TeamManagement.memberTeamsHelper')}
                      placeholder={t('TeamManagement.memberTeamsPlaceholder')}
                      clearable
                      getOptionDisabled={(option) => option === selectedTeamId}
                    />
                  )}
                />

                <Controller
                  name="scopeIds"
                  control={form.control}
                  render={({ field }) => (
                    <CuriosityTreeSelectMultipleField
                      ns={CuriosityNamespaceType.DATA_ACCESS_SCOPE}
                      value={field.value}
                      onChange={(value) => field.onChange(value ?? [])}
                      label={t('TeamManagement.scopesLabel')}
                      helperText={`${t('TeamManagement.scopesHelper')} ${t('TeamManagement.scopeInheritanceHint')}`}
                      placeholder={t('TeamManagement.scopesPlaceholder')}
                      clearable
                    />
                  )}
                />

                <div className="flex justify-end gap-2">
                  {isEditMode && (
                    <Button type="button" variant="destructive" onClick={handleDelete} disabled={saving || deleting}>
                      {deleting ? t('TeamManagement.deleting') : t('TeamManagement.delete')}
                    </Button>
                  )}

                  <Button type="submit" disabled={saving || deleting}>
                    {saving ? t('TeamManagement.saving') : t('TeamManagement.save')}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </WidgetContent>
      </Widget>
    </PageLayout>
  );
};
